import React from 'react'
import { useParams } from 'react-router'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton from '@divvy-web/skylab.basicbutton'
import { FormCheckboxInput, FormPhoneInput } from '@divvy-web/skylab.form'
import { useForm, useFormState, useWatch } from '@divvy-web/skylab.react-hook-form'
import Spinner from '@divvy-web/skylab.spinner'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import PrivacyPolicyLink from '@sputnik/components/PrivacyPolicyLink'
import TermsAndConditionsLink from '@sputnik/components/TermsAndConditionsLink'
import { useUpdateAuthorizedSignerPhoneNumber } from '@sputnik/components/VerifyPhoneNumber/EnterPhoneNumberModal/UpdateAuthSignerPhoneNumber.gql'
import {
  GetPhoneNumberForVerification,
  GetPhoneNumberForVerificationDocument,
  GetPhoneNumberForVerificationVariables,
  useGetPhoneNumberForVerificationLazyQuery,
} from '@sputnik/components/VerifyPhoneNumber/GetPhoneNumberForVerification.gql'
import { useRequestPhoneVerification } from '@sputnik/components/VerifyPhoneNumber/RequestPhoneVerification.gql'
import VerifyPhoneNumberModalView from '@sputnik/components/VerifyPhoneNumber/VerifyPhoneNumberModalView'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import {
  EnterPhoneNumberModalValidationSchema,
  enterPhoneNumberModalValidationSchema,
} from './enterPhoneNumberModalValidationSchema'
import styles from './styles.module.scss'

export interface EnterPhoneNumberModalProps {
  isShowing: boolean
  onConfirm: (phoneNumber: string) => void
  onClose: () => void
}

const EnterPhoneNumberModal = ({ isShowing, onConfirm, onClose }: EnterPhoneNumberModalProps) => {
  const [getClassName] = useNameFormatter('EnterPhoneNumberDrawerView')
  const { appId } = useParams()
  const showToast = useToast()

  const [getPhoneNumberForVerification] = useGetPhoneNumberForVerificationLazyQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const [updateAuthSignerPhoneNumber] = useUpdateAuthorizedSignerPhoneNumber()

  const [requestPhoneVerification, { client }] = useRequestPhoneVerification()

  const { handleSubmit, control } = useForm<EnterPhoneNumberModalValidationSchema>({
    mode: 'all',
    defaultValues: async () => {
      const { data } = await getPhoneNumberForVerification()

      const { phoneNumber, id, smsConsentGivenAt } = data?.creditApplication?.authorizedSigner ?? {}

      return {
        phoneNumber: phoneNumber ?? '',
        personId: id ?? '',
        smsConsentGiven: !!smsConsentGivenAt,
      }
    },
    resolver: yupResolver(enterPhoneNumberModalValidationSchema),
  })

  const { isValid, isLoading } = useFormState({ control })
  const smsConsentGiven = useWatch({ control, name: 'smsConsentGiven' })

  const onSubmit = async (data: EnterPhoneNumberModalValidationSchema) => {
    if (!appId) {
      return
    }

    const { personId, phoneNumber, smsConsentGiven } = data

    const { data: initialData } = await getPhoneNumberForVerification()

    const { phoneNumber: initalPhoneNumber } = initialData?.creditApplication?.authorizedSigner ?? {}

    if (initalPhoneNumber !== phoneNumber) {
      await updateAuthSignerPhoneNumber({
        variables: {
          creditApplicationId: appId,
          authorizedSigner: {
            id: personId,
            phoneNumber,
          },
        },
      })
    }

    await requestPhoneVerification({
      variables: {
        salesforceCreditId: appId,
        personId,
        phoneNumber,
        smsConsent: smsConsentGiven,
      },
      onError: (err) => {
        logError(err, {
          attributes: {
            action: 'handleSendSmsCode',
            creditAppId: appId,
            message: err?.message,
            result: 'Error requesting phone verification code',
          },
          eventName: 'RequestPhoneVerificationCode',
        })
        if (err?.message?.includes('exceeded rate limit')) {
          showToast(
            TOAST_TYPE_DANGER,
            <FormattedMessage
              defaultMessage='You have exceeded the rate limit for sending verification codes. Please try in a few minutes.'
              id='sputnik.EnterPhoneNumberModal__2XK5P0'
            />,
          )
        } else {
          showToast(
            TOAST_TYPE_DANGER,
            <FormattedMessage
              defaultMessage='Error requesting phone verification code'
              id='sputnik.EnterPhoneNumberModal__0ttUuJ'
            />,
          )
        }
      },
      onCompleted: () => {
        logInfo('handleSendSmsCode', {
          attributes: {
            creditAppId: appId,
            result: 'Send code button has been clicked',
          },
          eventName: 'RequestPhoneVerificationCode',
        })
        client.cache.updateQuery<GetPhoneNumberForVerification, GetPhoneNumberForVerificationVariables>(
          {
            query: GetPhoneNumberForVerificationDocument,
            variables: { creditApplicationId: appId },
          },
          (data) => {
            if (!data?.creditApplication?.authorizedSigner) {
              return data
            }

            return {
              ...data,
              creditApplication: {
                ...data.creditApplication,
                authorizedSigner: {
                  ...data.creditApplication.authorizedSigner,
                  smsConsentGivenAt: new Date(),
                },
              },
            }
          },
        )

        onConfirm(phoneNumber)
      },
    })
  }

  return (
    <VerifyPhoneNumberModalView
      actionButtons={
        <BasicButton
          disabled={isLoading || !isValid || !smsConsentGiven}
          onClick={handleSubmit(onSubmit)}
        >
          <FormattedMessage
            defaultMessage='Send code'
            id='sputnik.EnterPhoneNumberModal__MTwUmz'
          />
        </BasicButton>
      }
      isShowing={isShowing}
      title={
        <FormattedMessage
          defaultMessage='Verify your mobile number'
          id='sputnik.EnterPhoneNumberModal__jjCYZL'
        />
      }
      onClose={onClose}
    >
      {isLoading ? (
        <Spinner centered />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles[getClassName('prompt')]}>
            <FormattedMessage
              defaultMessage='We’ll send a verification code to your device.'
              id='sputnik.EnterPhoneNumberModal__4KccVz'
            />
          </div>
          <FormPhoneInput
            className='dd-privacy-mask'
            control={control}
            disabled={isLoading}
            label={
              <span className='dd-privacy-allow'>
                <FormattedMessage
                  defaultMessage='Mobile number'
                  id='sputnik.EnterPhoneNumberModal__JBLdZm'
                />
              </span>
            }
            name='phoneNumber'
          />
          <FormCheckboxInput
            caption={
              <FormattedMessage
                defaultMessage='Message & data rates may apply'
                id='sputnik.EnterPhoneNumberModal__pQcO7O'
              />
            }
            checkboxLabel={
              <FormattedMessage
                defaultMessage='I agree to receive SMS messages for authentication and account-related purposes. I agree to receive loan reminders, autopay alerts, and transaction updates. See our <tandc>Terms & Conditions</tandc> and <privacy>Privacy Policy</privacy>.'
                id='sputnik.EnterPhoneNumberModal__Y3cJhO'
                values={{
                  privacy: (chunk) => <PrivacyPolicyLink alternateLinkString={chunk} />,
                  tandc: (chunk) => <TermsAndConditionsLink alternateLinkString={chunk} />,
                }}
              />
            }
            className='dd-privacy-allow'
            control={control}
            disabled={isLoading}
            name='smsConsentGiven'
          />
        </form>
      )}
    </VerifyPhoneNumberModalView>
  )
}

export default EnterPhoneNumberModal
